import React from "react";
import {
  Box,
  Button,
  CardContent,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SaveIcon from "@mui/icons-material/Save";
import color from "../../components/utils/Colors";
import { TabPanel } from "./Settings";

// ── Validation schema ──────────────────────────────────────────────────────────
// All toggles are booleans — Yup just enforces their types.
const notificationSchema = Yup.object({
  emailNotifications: Yup.boolean().required(),
  pushNotifications: Yup.boolean().required(),
  courseUpdates: Yup.boolean().required(),
  promotionalEmails: Yup.boolean().required(),
  weeklyDigest: Yup.boolean().required(),
  newMessages: Yup.boolean().required(),
});

// ── Props ──────────────────────────────────────────────────────────────────────
interface NotificationValues {
  emailNotifications: boolean;
  pushNotifications: boolean;
  courseUpdates: boolean;
  promotionalEmails: boolean;
  weeklyDigest: boolean;
  newMessages: boolean;
}

interface NotificationTabProps {
  currentTab: number;
  initialValues: NotificationValues;
  onSave: (values: NotificationValues) => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const NotificationTab: React.FC<NotificationTabProps> = ({
  currentTab,
  initialValues,
  onSave,
}) => {
  return (
    <TabPanel value={currentTab} index={1}>
      <CardContent sx={{ padding: "30px" }}>
        <Formik
          initialValues={initialValues}
          validationSchema={notificationSchema}
          enableReinitialize
          onSubmit={(values) => onSave(values)}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              {/* ── Email Notifications ──────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Email Notifications
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.emailNotifications}
                      onChange={(e) =>
                        setFieldValue("emailNotifications", e.target.checked)
                      }
                    />
                  }
                  label="Receive email notifications"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Get notified about important account updates and activities
                </Typography>
              </Box>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.courseUpdates}
                      disabled={!values.emailNotifications}
                      onChange={(e) =>
                        setFieldValue("courseUpdates", e.target.checked)
                      }
                    />
                  }
                  label="Course updates and announcements"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Receive notifications when your instructors post new content
                </Typography>
              </Box>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.weeklyDigest}
                      disabled={!values.emailNotifications}
                      onChange={(e) =>
                        setFieldValue("weeklyDigest", e.target.checked)
                      }
                    />
                  }
                  label="Weekly learning digest"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Get a summary of your learning progress every week
                </Typography>
              </Box>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.newMessages}
                      disabled={!values.emailNotifications}
                      onChange={(e) =>
                        setFieldValue("newMessages", e.target.checked)
                      }
                    />
                  }
                  label="New messages"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Be notified when you receive new messages
                </Typography>
              </Box>

              <Divider sx={{ marginY: "30px" }} />

              {/* ── Push Notifications ───────────────────────────────── */}
              <Typography
                variant="h6"
                sx={{
                  color: color.textColor,
                  fontWeight: "bold",
                  marginBottom: "20px",
                }}
              >
                Push Notifications
              </Typography>

              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.pushNotifications}
                      onChange={(e) =>
                        setFieldValue("pushNotifications", e.target.checked)
                      }
                    />
                  }
                  label="Enable push notifications"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Receive instant notifications on your device
                </Typography>
              </Box>

              <Divider sx={{ marginY: "30px" }} />

              {/* ── Promotional ──────────────────────────────────────── */}
              <Box sx={{ marginBottom: "30px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.promotionalEmails}
                      onChange={(e) =>
                        setFieldValue("promotionalEmails", e.target.checked)
                      }
                    />
                  }
                  label="Promotional emails"
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#999", marginLeft: "32px" }}
                >
                  Receive special offers and course recommendations
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSubmitting}
                sx={{
                  background: color.firstColor,
                  color: "white",
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:hover": { background: color.forthColor },
                }}
              >
                Save Preferences
              </Button>
            </Form>
          )}
        </Formik>
      </CardContent>
    </TabPanel>
  );
};

export default NotificationTab;